import { Box, Button, Card, CardContent, CardHeader, IconButton, Input, InputAdornment, Tooltip, Dialog, DialogActions, DialogTitle, FormGroup, FormControlLabel, Switch } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { capitalize, debounce } from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { apiAuth, userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { EditOutlined } from "@mui/icons-material";
import { Loader } from "../../components/CustomLoader";
import { fDate } from "../../utils/formatTime";
import { Helmet } from "react-helmet-async";
import { calculateAge, TrimText } from "../../utils/helpers";

export default function Chat() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await userServices.getUser(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result);
          setTotalPages(res?.data?.data?.totalRecords);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await userServices.changeStatus({
        id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };



  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh ]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "100px"
    },
    {
      name: "User Name",
      sortable: true,
      selector: (row) => TrimText(row.username, 25),
    },
  
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email,
    },
    {
      name: "Phone",
      sortable: true,
      selector: (row) => row.phoneNumber,
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => capitalize(fDate(row.createdAt)),
    },
    {
      name: "Status",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === 1}
                onClick={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setWarning(
                    `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"
                    } this user?`
                  );
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </FormGroup>
      ),
    },
    {
      name: "Action",
      right: true,
      cell: (row) => (
        <>
          <Tooltip title="Edit" placement="left">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/chat/friends/${row?._id}`);
              }}
            >
               <VisibilityIcon />
            </IconButton>
          </Tooltip>
         
        </>
      ),
    },
  ];

  return (
    <Card>
      <Helmet>
        <title>Chat Management</title>
      </Helmet>
      <CardHeader title="Chat Management" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
     
        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />

        <Input
          value={searchItem}
          placeholder="Search"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  const cleared = "";
                  setSearchItem(cleared);
                  debouncedSearch(cleared);
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
      </Box>
      <CardContent>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
              },
            },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage) =>
            setPageSize(currentRowsPerPage)
          }
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        />
      </CardContent>
      <Dialog
        open={show}
        onClose={() => setShow(false)}
        className="popupbox-statis"
      >
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShow(false)}
            color="primary"
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
